import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { UploadInvoice } from "@/components/invoices/uploadInvoice";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useCreateInvoiceBo } from "@/api/invoice";
import { CreateInvoiceFormData, CreateInvoiceSchema } from "@/types/forms/bo/invoiceForm.form";
import { useGetCompanyBoListV1, useGetCompanyBOV1 } from "@/api/company.api";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { Modal } from "@/components/ui/customs/modal";
import { MONTHS_OPTIONS } from "@/constants/options/months.option";
import { FileUp } from "lucide-react";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { SelectV2 } from "@/components/ui/customs/select-v2";

type InvoiceUploadModalProps = {
  closeModal: () => void;
};

const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

export const InvoiceUploadModal = ({ closeModal }: InvoiceUploadModalProps) => {
  const { t } = useTranslation("boClients");
  const { mutateAsync: createInvoice, isSuccess } = useCreateInvoiceBo();

  const { data: companies } = useGetCompanyBoListV1();

  const createInvoiceForm = useForm<CreateInvoiceFormData>({
    defaultValues: {
      amountToBill: 0,
      billingEntityId: undefined,
      invoiceKey: undefined,
      month: undefined,
      year: undefined,
      totalTva: 0,
      totalHT: 0,
    },
    resolver: zodResolver(CreateInvoiceSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const companyIdWatch = createInvoiceForm.watch("companyId");
  const billingEntityIdWatch = createInvoiceForm.watch("billingEntityId");
  const totalHTWatch = createInvoiceForm.watch("totalHT");
  const totalTvaWatch = createInvoiceForm.watch("totalTva");

  const { data: company } = useGetCompanyBOV1(companyIdWatch);

  const handleUpload = async (key: string) => {
    createInvoiceForm.setValue("invoiceKey", key);
    const trigger = await createInvoiceForm.trigger();

    const { billingEntityId, month, year, amountToBill, invoiceKey, totalTva, totalHT } =
      createInvoiceForm.getValues();

    if (trigger) {
      createInvoice({
        billingEntityId,
        invoiceKey,
        year: Number(year),
        amountToBill: amountToBill * 100,
        totalTva: totalTva * 100,
        totalHT: totalHT * 100,
        month: Number(month),
      });
    }
  };

  const billingEntityWatch = createInvoiceForm.watch("billingEntityId");
  const amountToBillWatch = createInvoiceForm.watch("amountToBill");

  useEffect(() => {
    createInvoiceForm.setValue("totalHT", 0);
    createInvoiceForm.setValue("totalTva", 0);
  }, [billingEntityWatch, createInvoiceForm]);

  useEffect(() => {
    createInvoiceForm.setValue("amountToBill", (totalHTWatch || 0) + (totalTvaWatch || 0));
  }, [totalHTWatch, totalTvaWatch, createInvoiceForm]);

  useEffect(() => {
    if (isSuccess) {
      closeModal();
    }
  }, [isSuccess, closeModal]);

  return (
    <Modal
      open={true}
      onClose={closeModal}
      dismissible
      contentClassName="max-w-[600px]"
      title={
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-blue-100">
            <FileUp className="size-5 text-blue-600" />
          </div>
          <span>{t("invoiceImportModal.title")}</span>
        </div>
      }
      description={t("invoiceImportModal.description")}
    >
      <form className="flex w-full flex-col items-center gap-6">
        <SelectV2
          options={
            companies?.data.map((company) => ({
              value: company.id,
              label: company.businessName,
            })) || []
          }
          value={companyIdWatch ?? ""}
          onChange={(value) => {
            createInvoiceForm.setValue("companyId", value || "");
            createInvoiceForm.setValue("billingEntityId", "");
          }}
          placeholder={t("invoiceImportModal.companyPlaceholder")}
          wrapperClassName="h-full w-full"
          searchable
          clearable
        />
        {companyIdWatch && company && (
          <SelectV2
            key={companyIdWatch}
            options={
              company.billingEntities.map((billingEntity) => ({
                value: billingEntity.id,
                label: billingEntity.companyName,
              })) || []
            }
            value={billingEntityIdWatch ?? ""}
            onChange={(value) => createInvoiceForm.setValue("billingEntityId", value || "")}
            clearable={true}
            placeholder={t("invoiceImportModal.billingEntityPlaceholder")}
            wrapperClassName="h-full w-full"
          />
        )}
        {billingEntityIdWatch && (
          <>
            <div className="grid w-full grid-cols-3 items-center justify-between gap-4">
              <ControlledUnifiedInput
                control={createInvoiceForm.control}
                name="totalHT"
                placeholder={t("invoiceImportModal.totalHTLabel")}
                label={t("invoiceImportModal.totalHTLabel")}
                inputType="float"
              />
              <ControlledUnifiedInput
                control={createInvoiceForm.control}
                name="totalTva"
                placeholder={t("invoiceImportModal.totalTvaLabel")}
                label={t("invoiceImportModal.totalTvaLabel")}
                inputType="float"
              />
              <ControlledUnifiedInput
                control={createInvoiceForm.control}
                name="amountToBill"
                placeholder={t("invoiceImportModal.amountToBillLabel")}
                label={t("invoiceImportModal.amountToBillLabel")}
                inputType="float"
                disabled={true}
              />
            </div>
            <div className="items-top flex w-full flex-row justify-between gap-4">
              <ControlledSelectV2
                control={createInvoiceForm.control}
                name="year"
                options={years.map((e) => ({
                  value: e.toString(),
                  label: e.toString(),
                }))}
                placeholder={t("invoiceImportModal.yearLabel")}
                label={t("invoiceImportModal.yearLabel")}
                wrapperClassName="w-full"
              />
              <ControlledSelectV2
                control={createInvoiceForm.control}
                name="month"
                options={MONTHS_OPTIONS.map((e) => ({
                  value: e.value.toString(),
                  label: e.label,
                }))}
                placeholder={t("invoiceImportModal.monthLabel")}
                label={t("invoiceImportModal.monthLabel")}
                wrapperClassName="w-full"
              />
            </div>
          </>
        )}
        <div className="w-full max-w-[480px]">
          <UploadInvoice
            handleUpload={handleUpload}
            disabled={!billingEntityWatch || !amountToBillWatch}
          />
        </div>
      </form>
    </Modal>
  );
};
